import * as Graphics from "../graphics/graphics.js"

const resultViewPaper = Raphael(
    "result-view",
    document.getElementById("result-view").offsetWidth,
    document.getElementById("result-view").offsetHeight)

const debug = false

export function drawResultsView(project, results, forPrint) {

    if( results == null || Object.keys(results).length === 0 ) { return }

    const beam = project.beam
    
    if( beam.nodes.length <= 1 ) { return }

    document.getElementById("results-view-div").classList.remove('d-none')

    let paper = resultViewPaper

    paper.clear()

    const canvasW = forPrint === true ? 800 : Math.min(document.getElementById("result-view").offsetWidth, 900)
    const canvasH = 320

    const canvasRect = [canvasW, canvasH]

    paper.setSize(canvasW, canvasH)

    let el = document.getElementById('result-group-selector');
    const resultCaseSetLabel = el.options[el.selectedIndex].innerHTML;
    document.getElementById('plot-result-case-title').innerHTML = `(${resultCaseSetLabel}s)`

    const selectedLoadCombinations = getSelectedLoadCombinations(project)
    const selectedResultCaseSet = getSelectedResultCaseSet(results)
    
    let selectedResultCaseIndex = document.getElementById("numerical-results-selector").selectedIndex
    if(selectedResultCaseIndex < 0) { selectedResultCaseIndex = 0 }
    
    const resultCase = selectedResultCaseSet[selectedResultCaseIndex]

    //****************************
    // Make our displacement array
    //****************************
    const totalLength = beam.TotalLength()
    const xScale = canvasW / (1.2 * totalLength)
    const xStart = 0.05 * totalLength * xScale
    const yOffset = 100

    let maxAbsoluteDy = 1e-6
    for(let i in resultCase["displacements"]) {
        if(Math.abs(resultCase["displacements"][i]["dy"]) > maxAbsoluteDy) {
            maxAbsoluteDy = Math.abs(resultCase["displacements"][i]["dy"]);
        }
    }

    const xs = []
    const ys = []
    for(let i in resultCase["displacements"]) {
        xs.push(xStart + xScale * resultCase["displacements"][i]["x"]);
        ys.push(yOffset - resultCase["displacements"][i]["dy"] / maxAbsoluteDy * 50);
    }

    // gradient values; displacements, moments or shears
    const gradientValues = resultCase['displacements'].map( r => r['dy'] )
    
    Graphics.drawBeamSpanResultsView(xs, ys, gradientValues, paper)

    //************************
    // Legend
    //************************
    const legendWidth = 15
    const legendHeight = canvasRect[1] * 0.8
    const legendX = canvasRect[0] - 20
    const legendY = canvasRect[1] * 0.1

    // get max and min values
    let gradientMin = 1e9
    let gradientMax = -1e9
    gradientValues.forEach( val => {
        gradientMin = Math.min(gradientMin, val)
        gradientMax = Math.max(gradientMax, val)
    })

    if(Math.abs(gradientMin) < Math.abs(gradientMax)) {
        const v = gradientMin
        gradientMin = gradientMax
        gradientMax = v
    }
    const gradientMinLabel = showSmallLengthWithUnits(gradientMin)
    const gradientMaxLabel = showSmallLengthWithUnits(gradientMax)

    Graphics.drawResultViewLegend(legendX, legendY, legendWidth, legendHeight, gradientMinLabel, gradientMaxLabel, paper)

    //*******************
    // Reactions
    //*******************
    var nReactions = resultCase["node_reactions"].length
    var equation = selectedLoadCombinations["equations"][selectedResultCaseIndex]
    var loadCaseLabels = Object.keys(equation)
    
    for( var s = 0; s < nReactions; s++ ) {
        var x = resultCase["node_reactions"][s]["x"];
        if(project.beam.isNodeAtThisLocation(x) === false) {
            continue
        }

        // we also check to see if there is a point load directly over a support. the finite element method
        // will not show a reaction at this location so we manually add it in.
        // if there is a point load here and the support is fixed or pinned we apply our combination equation:
        //    sum(equation[loadCase] * pointLoadAtReaction[loadCase])
        var force = resultCase["node_reactions"][s]["fy"];
        var pointLoadAtReaction = project.beam.getPointLoadAtLocation(x);
        if(pointLoadAtReaction && project.beam.isSupportPinnedOrFixed(x)) {
            var additionalLoad = 0;
            loadCaseLabels.forEach( loadCaseLabel => {
                const factor = Number(equation[loadCaseLabel]);
                const load = Number(pointLoadAtReaction[loadCaseLabel]);
                if(!isNaN(factor) && !isNaN(load)) {
                    additionalLoad += factor * load;
                }
            });
            force += additionalLoad;
        }

        if(force !== 0) {
            force = (gUnits === ENGLISH_UNITS) ? force.toFixed(0) : Number(force * 4.44822).toFixed(0);
            var forceLabel = showBigPointLoad(force) + " " + bigPointLoadUnit();
            var direction = force > 0 ? "up" : "down";
            Graphics.drawForceReaction(direction, xStart + xScale * x, yOffset , forceLabel, paper);
        }

        var moment = resultCase["node_reactions"][s]["mz"];
        if(moment !== 0) {
            moment = (gUnits === ENGLISH_UNITS) ? moment.toFixed(0) : Number(moment * 0.11298482933333).toFixed(0);
            var momentLabel = showBigMoment(moment) + bigMomentUnit();
            Graphics.drawMomentReaction(xStart + xScale * x, yOffset, 0, momentLabel, paper);
        }
    }

    
    //*******************
    // Extremes
    //*******************
    const extremes = getExtremes(resultCase)
    const maxDy = extremes['maxDy']
    const maxFy = extremes['maxFy']
    const maxMz = extremes['maxMz']

    Graphics.drawExtremeDimension(xStart, xStart + maxDy[0] * xScale, yOffset + 100,
        "Max Dy = " + showSmallLengthWithUnits(maxDy[1]) + " @ " + showLengthWithUnits(maxDy[0]), paper)
    Graphics.drawExtremeDimension(xStart, xStart + maxFy[0] * xScale, yOffset + 140,
        "Max Shear = " + showBigPointLoad(maxFy[1]) + " " + bigPointLoadUnit() + " @ " + showLengthWithUnits(maxFy[0]), paper)
    Graphics.drawExtremeDimension(xStart, xStart + maxMz[0] * xScale, yOffset + 180,
        "Max Moment = " + showBigMoment(maxMz[1]) + " " + bigMomentUnit() + " @ " + showLengthWithUnits(maxMz[0]), paper)

}

function getSelectedLoadCombinations(project) {
    let resultLabel = document.getElementById("result-group-selector").value
    if( resultLabel == null ){ resultLabel = "load_case_results" }
    switch(resultLabel) {
        case "load_case_results":
            return project.design.loadCases
        case "combination_results":
            return project.design.loadCombination
        case "service_case_results":
            return project.design.serviceCases
        default:
            return null
    }
}

function getSelectedResultCaseSet(results) {
    let resultLabel = document.getElementById("result-group-selector").value
    if( resultLabel == null ){ resultLabel = "load_case_results" }
    return results[resultLabel]
}

export function onResultGroupChange(project, results) {
    const selectedLoadCombinations = getSelectedLoadCombinations(project)
    const selectedResultCaseSet = getSelectedResultCaseSet(results)
    
    var str = "<select id='numerical-results-selector'>";
    for( let i = 0; i < selectedResultCaseSet.length; i++ ) {
        const resultCase = selectedResultCaseSet[i];
        const equationIndex = parseInt(resultCase["equation_name"].split('EQ')[1]) - 1
        const equationName = selectedLoadCombinations["prettyEquations"][equationIndex]["EQ"]
        str += `<option value=${i}>${equationName}</option>`
    }
    str += "</select>";
    
    document.getElementById("numerical-results-selector-holder").innerHTML = str

    document.getElementById('numerical-results-selector').onchange = () => { 
        document.dispatchEvent(new CustomEvent('update-results-view'))
    }
}

function getExtremes(resultCase) {
    var maxDy = [0, 0];    //[index, value]
    var maxFy = [0, 0];
    var maxMz = [0, 0];

    for(var i = 0; i < resultCase["nodal_forces"].length; i++) {
        var x = Number(resultCase["nodal_forces"][i]['x']);
        var fy = Number(resultCase["nodal_forces"][i]['fy']);
        if(Math.abs(fy) > Math.abs(maxFy[1])) {
            maxFy = [x, fy];
        }
    }

    for( var i = 0; i < resultCase["nodal_forces"].length; i++ ) {
        var x = Number(resultCase["nodal_forces"][i]['x']);
        var mz = Number(resultCase["nodal_forces"][i]['mz']);
        if(Math.abs(mz) > Math.abs(maxMz[1])) {
            maxMz = [x, mz];
        }
    }

    for( var i = 0; i < resultCase["displacements"].length; i++ ) {
        var x = resultCase["displacements"][i]["x"];
        var dy = Number(resultCase["displacements"][i]["dy"]);
        if( Math.abs(dy) > Math.abs(maxDy[1])) {
            maxDy = [x, dy];
        }
    }

    return {'maxDy': maxDy, 'maxFy': maxFy, 'maxMz': maxMz}
}

export function showNumericalResultsTable(results) {
    document.getElementById('numerical-results-div').classList.remove('d-none')
    const selectedResultCaseSet = getSelectedResultCaseSet(results)

    let el = document.getElementById("numerical-results-selector")
    let selectedResultCaseIndex = el.selectedIndex
    
    if(selectedResultCaseIndex >= 0) {
        const selectedResultCaseSetLabel = el.options[selectedResultCaseIndex].innerHTML
        document.getElementById('numerical-results-equation-title').innerHTML = `(${selectedResultCaseSetLabel})`
    } else {
        selectedResultCaseIndex = 0 
    }

    const resultCase = selectedResultCaseSet[selectedResultCaseIndex]

    var max_dy = [0, 0];    //[index, value]
    var max_rz = [0, 0];
    var max_fy = [0, 0];
    var max_mz = [0, 0];

    var shears = [];
    var moments = [];

    var str = "<table class='table'>";
    str += "<tr class='ws-ui-table-header-row ws-ui-left'>";
    str += "<th>Location (" + smallLengthUnit() + ")</th>";
    str += "<th>Dy (" + smallLengthUnit() + ")</th>";
    if( debug )
        str += "<th>Rz (radians)</th>";
    str += "<th>Shear (" + smallPointLoadUnit() + ")</th>";
    str += "<th>Moment " + smallMomentUnit() + "</th>";

    for(var i = 0; i < resultCase["nodal_forces"].length; i++) {
        var x = Number(resultCase["nodal_forces"][i]['x']);
        var fy = Number(resultCase["nodal_forces"][i]['fy']);
        if(Math.abs(fy) > Math.abs(max_fy[1])) {
            max_fy = [x, fy];
        }
        shears.push(fy);
    }

    for( var i = 0; i < resultCase["nodal_forces"].length; i++ ) {
        var x = Number(resultCase["nodal_forces"][i]['x']);
        var mz = Number(resultCase["nodal_forces"][i]['mz']);
        if(Math.abs(mz) > Math.abs(max_mz[1])) {
            max_mz = [x, mz];
        }
        moments.push(mz.toFixed(2));
    }

    for( var i = 0; i < resultCase["displacements"].length; i++ ) {
        var x = resultCase["displacements"][i]["x"];
        var dy = Number(resultCase["displacements"][i]["dy"]);
        if( Math.abs(dy) > Math.abs(max_dy[1])) {
            max_dy = [x, dy];
        }

        str += "<tr class='ws-ui-table-row'>";
        str += "<td>" + showSmallLength(x).toFixed(2) + "</td>";
        str += "<td>" + showSmallLength(dy).toFixed(2)  + "</td>";
        var vy = shears[i];
        var mz = moments[i];

        str += "<td>" + showSmallPointLoad(vy) + "</td>";
        str += "<td>" + showSmallMoment(mz) + "</td>";
        str += "</tr>";
    }
    str += "</table>";

    document.getElementById("beam-numerical-results-table").innerHTML = str

    var max_str = "<strong>Extremes</strong>";
    max_str += "<table class='table'>";
    max_str += "<tr class='ws-ui-table-header-row '>";
    max_str += "<th class='ws-ui-left ws-ui-table-150'>Type</th>";
    max_str += "<th>Location</th>";
    max_str += "<th>Value</th>";

    var i = max_dy[0];
    max_str += "<tr class='ws-ui-table-row'>";
    max_str += "<th class='ws-ui-left ws-ui-table-150'>Max dy</th>";
    max_str += "<td>" + showLengthWithUnits(max_dy[0]) + "</td>";
    max_str += "<td>" + showSmallLengthWithUnits(max_dy[1]) + "</td>";
    max_str += "</tr>";

    i = max_fy[0];
    max_str += "<tr class='ws-ui-table-row'>";
    max_str += "<th class='ws-ui-left ws-ui-table-150'>Max Shear</th>";
    max_str += "<td>" + showLengthWithUnits(max_fy[0]) + "</td>";
    max_str += "<td>" + showBigPointLoad(max_fy[1]) + " " + bigPointLoadUnit() + "</td>";
    max_str += "</tr>";

    //showBigMoment(controlling_Mu) + bigMomentUnit()
    i = max_mz[0];
    max_str += "<tr class='ws-ui-table-row'>";
    max_str += "<th class='ws-ui-left ws-ui-table-150'>Max Moment</th>";
    max_str += "<td>" + showLengthWithUnits(max_mz[0]) + "</td>";
    max_str += "<td>" + showBigMoment(max_mz[1]) + " " + bigMomentUnit() + "</td>";
    max_str += "</tr>";

    max_str += "</table><br>";

    document.getElementById('extreme-results-table').innerHTML = max_str
}

var previousPoint = null;
function showTooltip(x, y, contents) {
    $('<div id="tooltip">' + contents + '</div>').css( {
        position: 'absolute',
        display: 'none',
        top: y + 5,
        left: x + 5,
        border: '1px solid #fdd',
        padding: '2px',
        'background-color': '#fee',
        opacity: 0.80
    }).appendTo("body").fadeIn(200);
}

function toolTipHover(event, pos, item) {
    if (item) {
        if (previousPoint != item.datapoint) {
            previousPoint = item.datapoint;

            $("#tooltip").remove();
            var x = item.datapoint[0].toFixed(2),
                y = item.datapoint[1].toFixed(3);

            showTooltip(item.pageX, item.pageY,
                        item.series.label + " @ " + x + " = " + y);
        }
    } else {
        $("#tooltip").remove();
        previousPoint = null;
    }
}

export function showResultCharts(project, results, forPrint) {
    
    document.getElementById('plots-div').classList.remove('d-none')

    let str = String.raw`
        <div class="section-header">Dy ${gUnits === ENGLISH_UNITS ? '(inches)' : '(mm)'}</div>
        <div id="beam-disp-graph" style="height:250px;"></div>
        <div class="section-header">Shear ${gUnits === ENGLISH_UNITS ? '(lbs)' : '(N)'}</div>
        <div id="beam-shear-graph" style="height:250px;"></div>
        <div class="section-header">Moment ${gUnits === ENGLISH_UNITS ? '(lb-in)' : '(N-m)'}</div>
        <div id="beam-moment-graph" style="height:250px;"></div>
        <div class="section-header">Legend</div>
        <div id="beam-graph-legend" class="table"></div>`

    document.getElementById('plot-panel-area').innerHTML = str
    updateResultCharts(project, results, forPrint)
}

function updateResultCharts(project, results) {
    let caseSet = getSelectedLoadCombinations(project)
    let resultCases = getSelectedResultCaseSet(results)

    function ShowDispChart( item, graph_element, show_legend ){
        var options = {
            legend: { show: show_legend, noColumns: 2, container:$("#beam-graph-legend") },
            lines: { show: true },
            points: { show: true },
            grid: { hoverable: true }
        };
        var d = [];
        for(var i = 0; i < resultCases.length; i++) {
            const resultCase = resultCases[i];
            var res = resultCase["displacements"];
            var d1 = [];
            for(var j = 0; j < res.length; j++) {
                d1.push(
                    [showDistanceLength(Number(res[j]["x"])),
                    showSmallLength(Number(res[j][item])).toFixed(4)]);
            }
            const equationIndex = parseInt(resultCase["equation_name"].split('EQ')[1]) - 1
            const equationName = caseSet["prettyEquations"][equationIndex]["EQ"]
            d.push( {label: equationName, data: d1 } )
        }
        $(graph_element).bind("plothover", toolTipHover);
        $.plot($(graph_element), d, options);
    }

    ShowDispChart( "dy", "#beam-disp-graph", true );
    if( debug )
        ShowDispChart( "rz", "#beam-theta-graph", false );

    function ShowForceChart( item, graph_element ){
        var options = {
            lines: { show: true },
            legend: { show: false },
            points: { show: true },
            grid: { hoverable: true }
        };
        var d = [];
        for( var i = 0; i < resultCases.length; i++ ) {
            const resultCase = resultCases[i];
            var res = resultCase["nodal_forces"];
            var d1 = [];
            for( var j = 0; j < res.length; j++ ) {
                d1.push([Number(res[j]["x"]), Number(res[j][item]).toFixed(4)]);
            }
            const equationIndex = parseInt(resultCase["equation_name"].split('EQ')[1]) - 1
            const equationName = caseSet["prettyEquations"][equationIndex]["EQ"]
            d.push( {label: equationName, data: d1 } )
        }
        $(graph_element).bind("plothover", toolTipHover);
        $.plot($(graph_element), d, options);
    }
    ShowForceChart( "fy", "#beam-shear-graph" );
    ShowForceChart( "mz", "#beam-moment-graph" );
}

export function showReactionsSummary(project, results) {
    document.getElementById('beam-reactions-div').classList.remove('d-none')

    const loadCaseResults = results["load_case_results"]
    let reactions = loadCaseResults[0]["node_reactions"]
    let units = (gUnits === ENGLISH_UNITS) ? "(lbs)" : "(N)"
    const loadCases = ["Dead", "Live", "Live Roof", "Snow", "Wind", "Earth", "Lateral (H)", "Roof"]

    let str = `<div class='fs-6 text-strong'>Fy Reactions ${units}</div>`
    
    str += `<table class="table">`

    // loop over supports and build the table header
    let tableHeaderStr = "<tr><th>Load Case</th>"
    for(let s = 0; s < reactions.length; s++ ) {
        const x = reactions[s]["x"]
        if(project.beam.isNodeAtThisLocation(x) === false) {
            continue
        }
        tableHeaderStr += `<th>${showLengthWithUnits(x)}</th>`
    }
    tableHeaderStr += '</tr>'

    str += tableHeaderStr

    for(let lcr = 0; lcr < loadCaseResults.length; lcr++) {
        reactions = loadCaseResults[lcr]["node_reactions"]
        const index = reactions.findIndex(r => project.beam.isNodeAtThisLocation(r["x"]) && r["fy"] !== 0)
        if(index < 0) { continue }
        str += `<tr><th>${loadCases[lcr]}</th>`
        for(let s = 0; s < reactions.length; s++ ) {
            const x = reactions[s]["x"]
            const fy = reactions[s]["fy"]
            if(project.beam.isNodeAtThisLocation(x) === false) {
                continue
            }
            const res = (gUnits === ENGLISH_UNITS) ? fy.toFixed(0) : Number(fy * 4.44822).toFixed(0);
            str += `<td>${res}</td>`
        }
        str += '</tr>'
    }

    str += "</table>"

    // moment
    let hasMoment = false
    for(let lcr = 0; lcr < loadCaseResults.length; lcr++) {
        reactions = loadCaseResults[lcr]["node_reactions"]
        const index = reactions.findIndex(r => project.beam.isNodeAtThisLocation(r["x"]) && r["mz"] !== 0)
        if(index >= 0) {
            hasMoment = true
            break
        }
    }

    if(hasMoment) {

        units = (gUnits === ENGLISH_UNITS) ? "(lb-in)" : "(N-m)";
        str += `<div class='fs-6 text-strong'>Mz Reactions ${units}</div>`
        str += `<table class="table">`
        str += tableHeaderStr

        const loadCases = ["Dead", "Live", "Live Roof", "Snow", "Wind", "Earth", "Pressure (H)", "Roof"]
        for(let lcr = 0; lcr < loadCaseResults.length; lcr++) {
            reactions = loadCaseResults[lcr]["node_reactions"]
            const index = reactions.findIndex(r => project.beam.isNodeAtThisLocation(r["x"]) && r["mz"] !== 0)
            if(index < 0) { continue }
            str += `<tr><th>${loadCases[lcr]}</th>`
            for(let s = 0; s < reactions.length; s++ ) {
                const x = reactions[s]["x"]
                const mz = reactions[s]["mz"]
                if(project.beam.isNodeAtThisLocation(x) === false) {
                    continue
                }
                var res = (gUnits === ENGLISH_UNITS) ? mz.toFixed(0) : Number(mz * 0.11298482933333).toFixed(0);
                str += `<td>${res}</td>`
            }
            str += '</tr>'
        }
        str += "</table>"
    }
    
    document.getElementById('beam-reactions-table').innerHTML = str
}